/**
 * usePurchaseOrderLifecycleSteps Hook
 *
 * Reads the automation settings of the purchase order steps and turns them into the
 * step model both lifecycle layouts render.
 */
import { useQuery } from '@apollo/client/react';
import { ApprovalMode, DEFAULT_PURCHASE_ORDER_COMPLETION } from '@insio/shared-types';
import {
  PurchaseOrderApprovalSettingsDocument,
  PurchaseOrderAutomationSettingsDocument
} from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { APPROVAL_MODE_LABEL_KEY, completionSummaryKeys, type LifecycleStep } from './types';

export function usePurchaseOrderLifecycleSteps(): LifecycleStep[] {
  const t = useFeatureTranslation('systemSettings.automation.lifecycle');
  const tClosure = useFeatureTranslation('systemSettings.automation.purchaseOrderClosure');

  const { data: approvalData } = useQuery(PurchaseOrderApprovalSettingsDocument);
  const { data: automationData } = useQuery(PurchaseOrderAutomationSettingsDocument);

  // Defaults mirror the backend: the approval is on until somebody switches it off.
  const approvalMode = approvalData?.purchaseOrderApprovalSettings.mode ?? ApprovalMode.AI_AND_USER;
  const completion = automationData?.purchaseOrderAutomationSettings ?? {
    completionBaseStatus: DEFAULT_PURCHASE_ORDER_COMPLETION.baseStatus,
    isDeliveryRequired: DEFAULT_PURCHASE_ORDER_COMPLETION.isDeliveryRequired,
    isInvoicingRequired: DEFAULT_PURCHASE_ORDER_COMPLETION.isInvoicingRequired
  };

  return [
    {
      key: 'validation',
      icon: 'list-ul',
      isLocked: true,
      stateLabel: t('alwaysOn')
    },
    {
      key: 'approval',
      icon: 'circle-check',
      isLocked: false,
      isEnabled: approvalMode !== ApprovalMode.SKIPPED,
      stateLabel: t(APPROVAL_MODE_LABEL_KEY[approvalMode])
    },
    {
      key: 'closure',
      icon: 'box',
      isLocked: false,
      isEnabled: true,
      stateLabel: completionSummaryKeys(completion)
        .map(key => tClosure(key))
        .join(' + ')
    }
  ];
}
